import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  FaTruck,
  FaExchangeAlt,
  FaLeaf,
  FaIndustry,
  FaFacebookF,
  FaInstagram,
  FaTwitter,
  FaHeart,
  FaRegHeart,
  FaStar,
  FaRegStar,
  FaRuler,
  FaChevronDown,
  FaChevronUp,
  FaShieldAlt,
  FaRecycle,
  FaFlask,
} from "react-icons/fa";
import {
  MdVerified,
  MdContentCopy,
  MdCheck,
  MdLocalShipping,
  MdSwapHoriz,
} from "react-icons/md";
import { useApp } from "../App";
import { BASE_URL } from "../config";
import ProductCard from "../components/ProductCard";
import api from "../services/api";
import "../styles/ProductPage.css";
import { useAuthGuard } from "../hooks/useAuthGuard";

// ✅ Helper to normalize image URLs - handles both full URLs and relative paths
const normalizeImage = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BASE_URL}${url}`;
};

/* ─── Size Guide Modal ───────────────────────────────────── */
const SIZE_GUIDE = [
  { name: "Regular (2.0m)", waist: "28–36", length: "200cm", weight: "200g" },
  { name: "Large (2.25m)", waist: "34–40", length: "225cm", weight: "225g" },
  { name: "XL (2.50m)", waist: "38–44", length: "250cm", weight: "250g" },
];

const DEFAULT_COLORS = [
  { name: "Classic White", hex: "#F5F5F0" },
  { name: "Midnight Blue", hex: "#1B2D5B" },
  { name: "Terracotta", hex: "#C45C3A" },
  { name: "Forest Green", hex: "#2D5A3D" },
  { name: "Saffron", hex: "#E8941A" },
  { name: "Charcoal", hex: "#3D3D3D" },
];

function SizeGuideModal({ onClose }) {
  return (
    <div className="pd-modal-backdrop" onClick={onClose}>
      <div className="pd-modal-box" onClick={(e) => e.stopPropagation()}>
        <div className="pd-modal-header">
          <h3>Size Guide</h3>
          <button className="pd-modal-close" onClick={onClose}>
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <p className="pd-modal-subtitle">
          Measurements are approximate. When in doubt, size up.
        </p>
        <table className="pd-size-guide-table">
          <thead>
            <tr>
              <th>Size</th>
              <th>Waist (inches)</th>
              <th>Length</th>
              <th>Fabric Weight</th>
            </tr>
          </thead>
          <tbody>
            {SIZE_GUIDE.map((s) => (
              <tr key={s.name}>
                <td>
                  <strong>{s.name}</strong>
                </td>
                <td>{s.waist}"</td>
                <td>{s.length}</td>
                <td>{s.weight}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="pd-modal-tip">
          <span className="pd-modal-tip-icon">💡</span>
          <span>
            <strong>Tip:</strong> Measure around the fullest part of your hips,
            not your waist, for the best fit in a lungi.
          </span>
        </p>
      </div>
    </div>
  );
}

/* ─── Trust Badges Strip ─────────────────────────────────── */
const TRUST_BADGES = [
  { icon: <FaLeaf />, label: "100% Natural" },
  { icon: <FaShieldAlt />, label: "No Harmful Chemicals" },
  { icon: <FaRecycle />, label: "Eco Friendly" },
  { icon: <FaFlask />, label: "Dermat Tested" },
  { icon: <FaIndustry />, label: "Made in India" },
];

function TrustBadges() {
  return (
    <div className="pd-trust-strip">
      {TRUST_BADGES.map((b, i) => (
        <div className="pd-trust-item" key={i}>
          <span className="pd-trust-icon">{b.icon}</span>
          <span className="pd-trust-label">{b.label}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── How to Use Steps ───────────────────────────────────── */
const HOW_TO_USE = [
  {
    step: "01",
    title: "Soak & Drape",
    desc: "Soak in cold water for 5 mins to soften the weave before first use.",
  },
  {
    step: "02",
    title: "Wrap & Tuck",
    desc: "Hold one end at the centre, wrap around the waist and tuck securely.",
  },
  {
    step: "03",
    title: "Style Your Way",
    desc: "Fold the top edge for a neater look or leave full-length for traditional style.",
  },
];

function HowToUse() {
  return (
    <div className="pd-howto-section">
      <div className="pd-howto-head">
        <span className="pd-section-eyebrow">Usage</span>
        <h4 className="pd-howto-title">How to Wear</h4>
      </div>
      <div className="pd-howto-steps">
        {HOW_TO_USE.map((s) => (
          <div className="pd-howto-step" key={s.step}>
            <span className="pd-howto-num">{s.step}</span>
            <div className="pd-howto-text">
              <strong>{s.title}</strong>
              <p>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Key Ingredients ────────────────────────────────────── */
const INGREDIENTS = [
  { emoji: "🌿", name: "Pure Cotton", benefit: "Breathable & hypoallergenic" },
  { emoji: "🎨", name: "Reactive Dye", benefit: "Vivid colour, 40+ wash fast" },
  { emoji: "🧵", name: "60s Count Yarn", benefit: "Ultra-fine, soft on skin" },
  { emoji: "♻️", name: "Pre-shrunk", benefit: "Maintains size after wash" },
];

function KeyIngredients() {
  return (
    <div className="pd-ingredients-section">
      <div className="pd-ingredients-head">
        <span className="pd-section-eyebrow">Craftsmanship</span>
        <h4 className="pd-ingredients-title">What Makes It Special</h4>
      </div>
      <div className="pd-ingredients-grid">
        {INGREDIENTS.map((ing) => (
          <div className="pd-ingredient-card" key={ing.name}>
            <span className="pd-ingredient-emoji">{ing.emoji}</span>
            <strong className="pd-ingredient-name">{ing.name}</strong>
            <p className="pd-ingredient-benefit">{ing.benefit}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Fabric Story Accordion ─────────────────────────────── */
const FABRIC_STORY = [
  {
    title: "Origin & Weave",
    content:
      "Woven on traditional pit looms in the Erode district of Tamil Nadu, this fabric uses a plain weave construction giving it that signature lightweight drape unique to South Indian cotton.",
  },
  {
    title: "Thread Count & Feel",
    content:
      "60s count combed cotton yarn. The higher the count, the finer and softer the fabric. After 5–6 washes this cloth becomes noticeably softer — a quality machine-spun fabric simply cannot replicate.",
  },
  {
    title: "Dye & Colour Process",
    content:
      "Reactive dyes are used for sharp, vivid colours that won't fade. The fabric is pre-shrunk and colour-fast tested to withstand 40+ washes without significant colour loss.",
  },
  {
    title: "Care Instructions",
    content:
      "Machine wash cold, gentle cycle. Do not bleach. Tumble dry low or line dry in shade. Iron on medium heat while slightly damp for best results.",
  },
];

function FabricStory() {
  const [open, setOpen] = useState(null);
  return (
    <div className="pd-fabric-story">
      <div className="pd-fabric-story-head">
        <span className="pd-section-eyebrow">Craftsmanship</span>
        <h4 className="pd-fabric-story-title">The Fabric Story</h4>
      </div>
      {FABRIC_STORY.map((item, i) => (
        <div
          key={i}
          className={`pd-fabric-accordion ${open === i ? "is-open" : ""}`}
        >
          <button
            className="pd-fabric-accordion-head"
            onClick={() => setOpen(open === i ? null : i)}
          >
            <span>{item.title}</span>
            <span
              className={`pd-fabric-chevron ${open === i ? "is-open" : ""}`}
            >
              {open === i ? (
                <FaChevronUp size={11} />
              ) : (
                <FaChevronDown size={11} />
              )}
            </span>
          </button>
          <div className="pd-fabric-accordion-body">
            <p>{item.content}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Image Zoom Panel ───────────────────────────────────── */
function ZoomableImage({ src, alt }) {
  const [zoomed, setZoomed] = useState(false);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const imgRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    setPos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const imageSrc = normalizeImage(src);

  return (
    <div
      ref={imgRef}
      className={`pd-zoomable ${zoomed ? "is-zoomed" : ""}`}
      onMouseEnter={() => setZoomed(true)}
      onMouseLeave={() => setZoomed(false)}
      onMouseMove={handleMouseMove}
    >
      <img
        src={imageSrc}
        alt={alt}
        style={
          zoomed
            ? {
                transformOrigin: `${pos.x}% ${pos.y}%`,
                transform: "scale(2.2)",
              }
            : {}
        }
      />
      {!zoomed && (
        <div className="pd-zoom-hint">
          <svg
            viewBox="0 0 24 24"
            width="13"
            height="13"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" strokeLinecap="round" />
          </svg>
          <span>Hover to zoom</span>
        </div>
      )}
    </div>
  );
}

/* ─── Coupon Reveal ──────────────────────────────────────── */
function CouponStrip({ showToast, availableCoupons, loadingCoupons }) {
  const [copied, setCopied] = useState(null);

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(code);
    showToast?.(`Coupon ${code} copied!`);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loadingCoupons) {
    return (
      <div className="pd-coupon-strip">
        <p className="pd-coupon-strip-title">
          <svg
            viewBox="0 0 24 24"
            width="13"
            height="13"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
            <line
              x1="7"
              y1="7"
              x2="7.01"
              y2="7"
              strokeLinecap="round"
              strokeWidth="3"
            />
          </svg>
          Available Coupons
        </p>
        <div className="pd-coupon-skeleton">
          <div className="pd-skeleton-line"></div>
          <div className="pd-skeleton-line"></div>
          <div className="pd-skeleton-line"></div>
        </div>
      </div>
    );
  }

  if (!availableCoupons || availableCoupons.length === 0) return null;

  const formatDiscount = (coupon) => {
    if (coupon.discountType === "percentage")
      return `${coupon.discountValue}% OFF`;
    if (coupon.discountType === "fixed") return `₹${coupon.discountValue} OFF`;
    return "OFF";
  };

  const formatMinOrder = (minOrderValue) => {
    if (!minOrderValue || minOrderValue === 0) return "No minimum";
    return `Min. ₹${minOrderValue.toLocaleString()}`;
  };

  return (
    <div className="pd-coupon-strip">
      <p className="pd-coupon-strip-title">
        <svg
          viewBox="0 0 24 24"
          width="13"
          height="13"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <line
            x1="7"
            y1="7"
            x2="7.01"
            y2="7"
            strokeLinecap="round"
            strokeWidth="3"
          />
        </svg>
        Available Coupons
      </p>
      <div className="pd-coupon-list">
        {availableCoupons.map((coupon) => (
          <div key={coupon.code} className="pd-coupon-card">
            <div className="pd-coupon-left">
              <span className="pd-coupon-code">{coupon.code}</span>
              <span className="pd-coupon-desc">
                {coupon.description || formatDiscount(coupon)}
              </span>
              <span className="pd-coupon-min">
                {formatMinOrder(coupon.minOrderValue)}
              </span>
            </div>
            <button
              className={`pd-coupon-copy-btn ${copied === coupon.code ? "is-copied" : ""}`}
              onClick={() => handleCopy(coupon.code)}
            >
              {copied === coupon.code ? (
                <MdCheck size={13} />
              ) : (
                <MdContentCopy size={13} />
              )}
              {copied === coupon.code ? "Copied" : "Copy"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Delivery Info Bar ──────────────────────────────────── */
function DeliveryBar() {
  return (
    <div className="pd-delivery-bar">
      <div className="pd-dbar-item">
        <MdLocalShipping size={18} />
        <div>
          <strong>Free Delivery</strong>
          <span>Orders above ₹1,000</span>
        </div>
      </div>
      <div className="pd-dbar-divider" />
      <div className="pd-dbar-item">
        <MdSwapHoriz size={18} />
        <div>
          <strong>7-Day Returns</strong>
          <span>Easy & hassle-free</span>
        </div>
      </div>
      <div className="pd-dbar-divider" />
      <div className="pd-dbar-item">
        <MdVerified size={18} />
        <div>
          <strong>Authentic</strong>
          <span>Tamil Nadu mills</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Authenticity Badge ─────────────────────────────────── */
function AuthenticityBadge() {
  return (
    <div className="pd-auth-badge">
      <div className="pd-auth-badge-icon">
        <MdVerified size={18} />
      </div>
      <div className="pd-auth-text">
        <span className="pd-auth-title">Authenticity Guaranteed</span>
        <span className="pd-auth-sub">Directly woven in Tamil Nadu mills</span>
      </div>
    </div>
  );
}

/* ─── Review Summary Bar ─────────────────────────────────── */
function ReviewSummary({ reviews }) {
  if (!reviews || reviews.length === 0) return null;
  const avg = (
    reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
  ).toFixed(1);
  const counts = [5, 4, 3, 2, 1].map((n) => ({
    star: n,
    count: reviews.filter((r) => Math.round(r.rating) === n).length,
  }));
  return (
    <div className="pd-review-summary">
      <div className="pd-rs-score">
        <span className="pd-rs-number">{avg}</span>
        <div className="pd-rs-stars">
          {[...Array(5)].map((_, i) =>
            i < Math.round(avg) ? <FaStar key={i} /> : <FaRegStar key={i} />,
          )}
        </div>
        <span className="pd-rs-total">{reviews.length} reviews</span>
      </div>
      <div className="pd-rs-bars">
        {counts.map(({ star, count }) => (
          <div key={star} className="pd-rs-row">
            <span className="pd-rs-label">{star}★</span>
            <div className="pd-rs-track">
              <div
                className="pd-rs-fill"
                style={{
                  width: `${reviews.length ? (count / reviews.length) * 100 : 0}%`,
                }}
              />
            </div>
            <span className="pd-rs-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Sticky Cart Bar ────────────────────────────────────── */
function StickyCartBar({
  product,
  selectedPrice,
  selectedSize,
  qty,
  onAddToCart,
  isInStock,
}) {
  const [visible, setVisible] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: "0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div ref={sentinelRef} className="pd-sticky-sentinel" />
      <div
        className={`pd-sticky-bar ${visible ? "pd-sticky-bar--visible" : ""}`}
      >
        <div className="pd-sticky-bar-inner">
          <div className="pd-sticky-bar-info">
            <span className="pd-sticky-bar-name">{product?.name}</span>
            <span className="pd-sticky-bar-meta">
              {selectedSize && <span>{selectedSize}</span>}
              <span className="pd-sticky-bar-price">
                ₹{selectedPrice?.toLocaleString()}
              </span>
            </span>
          </div>
          <button
            className="pd-sticky-bar-btn"
            onClick={onAddToCart}
            disabled={!isInStock}
          >
            {isInStock ? (
              <>
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                >
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                Add to Cart
              </>
            ) : (
              "Out of Stock"
            )}
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── Recently Viewed Products ─────────────────────────────────── */
function RecentlyViewed({ currentProductId }) {
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRecentlyViewed = async () => {
      try {
        const stored = localStorage.getItem("recently_viewed");
        if (!stored) {
          setLoading(false);
          return;
        }
        const recentIds = JSON.parse(stored);
        const filteredIds = recentIds
          .filter((id) => id !== currentProductId)
          .slice(0, 4);
        if (filteredIds.length === 0) {
          setRecentProducts([]);
          setLoading(false);
          return;
        }
        const productPromises = filteredIds.map((id) =>
          api.products.getById(id).catch(() => null),
        );
        const products = await Promise.all(productPromises);
        setRecentProducts(products.filter((p) => p !== null));
      } catch (error) {
        console.error("Error loading recently viewed:", error);
      } finally {
        setLoading(false);
      }
    };
    loadRecentlyViewed();
  }, [currentProductId]);

  if (loading || recentProducts.length === 0) return null;

  return (
    <section className="pd-related-section pd-recent-section">
      <div className="pd-related-header">
        <span className="pd-related-eyebrow">
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          Recently Viewed
        </span>
        <h2 className="pd-related-title">Back for Another Look?</h2>
        <p className="pd-recent-subtitle">Products you've browsed recently</p>
      </div>
      <div className="pd-related-grid">
        {recentProducts.map((p) => (
          <ProductCard key={p._id} product={p} />
        ))}
      </div>
    </section>
  );
}

/* ─── You May Also Like ─────────────────────────────── */
function YouMayAlsoLike({ currentProductId, currentCategory, currentTags }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRecommendations = async () => {
      setLoading(true);
      try {
        const response = await api.products.getAll({
          category: currentCategory,
          limit: 8,
          exclude: currentProductId,
        });
        let products = (response.products || []).filter(
          (p) => p._id !== currentProductId,
        );
        if (currentTags?.length > 0) {
          products = products
            .map((product) => {
              const matchScore = (product.tags || []).filter((tag) =>
                currentTags.some(
                  (ct) => ct.toLowerCase() === tag.toLowerCase(),
                ),
              ).length;
              return { ...product, matchScore };
            })
            .sort((a, b) => b.matchScore - a.matchScore);
        }
        setRecommendations(products.slice(0, 6));
      } catch (error) {
        try {
          const fallback = await api.products.getAll({
            category: currentCategory,
            limit: 6,
          });
          setRecommendations(
            (fallback.products || [])
              .filter((p) => p._id !== currentProductId)
              .slice(0, 6),
          );
        } catch (e) {
          setRecommendations([]);
        }
      } finally {
        setLoading(false);
      }
    };
    loadRecommendations();
  }, [currentProductId, currentCategory, currentTags]);

  if (loading) {
    return (
      <section className="pd-related-section">
        <div className="pd-loading-recommendations">
          <div className="pd-spinner-small" />
          <span>Finding perfect matches...</span>
        </div>
      </section>
    );
  }
  if (recommendations.length === 0) return null;

  return (
    <section className="pd-related-section">
      <div className="pd-related-header">
        <span className="pd-related-eyebrow">
          <svg
            viewBox="0 0 24 24"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
          Curated for You
        </span>
        <h2 className="pd-related-title">You May Also Like</h2>
        <p className="pd-recent-subtitle">
          Handpicked recommendations based on your style
        </p>
      </div>
      <div className="pd-related-grid">
        {recommendations.map((p) => (
          <ProductCard key={p._id} product={p} />
        ))}
      </div>
    </section>
  );
}

const saveToRecentlyViewed = (productId) => {
  try {
    const stored = localStorage.getItem("recently_viewed");
    let recent = stored ? JSON.parse(stored) : [];
    recent = recent.filter((id) => id !== productId);
    recent.unshift(productId);
    recent = recent.slice(0, 8);
    localStorage.setItem("recently_viewed", JSON.stringify(recent));
  } catch (error) {
    console.error("Error saving to recently viewed:", error);
  }
};

// Helper function to parse product data
const parseProductData = (product) => {
  if (!product) return product;

  const parsed = { ...product };

  if (parsed.sizes && typeof parsed.sizes === "string") {
    try {
      parsed.sizes = JSON.parse(parsed.sizes);
    } catch {
      parsed.sizes = [];
    }
  }
  if (parsed.images && typeof parsed.images === "string") {
    try {
      parsed.images = JSON.parse(parsed.images);
    } catch {
      parsed.images = [];
    }
  }
  if (parsed.reviews && typeof parsed.reviews === "string") {
    try {
      parsed.reviews = JSON.parse(parsed.reviews);
    } catch {
      parsed.reviews = [];
    }
  } else if (!parsed.reviews) {
    parsed.reviews = [];
  }
  if (parsed.tags && typeof parsed.tags === "string") {
    try {
      parsed.tags = JSON.parse(parsed.tags);
    } catch {
      parsed.tags = [];
    }
  }
  if (parsed.colors && typeof parsed.colors === "string") {
    try {
      parsed.colors = JSON.parse(parsed.colors);
    } catch {
      parsed.colors = [];
    }
  }

  return parsed;
};

/* ─── Main Component ─────────────────────────────────────── */
export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, toggleWishlist, wishlist, showToast, user } = useApp();
  const { requireAuth } = useAuthGuard();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeTab, setActiveTab] = useState("desc");
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [submittingRev, setSubmittingRev] = useState(false);
  const [cartState, setCartState] = useState("idle");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0);

  useEffect(() => {
    fetchAvailableCoupons();
  }, []);

  useEffect(() => {
    if (product && product.reviews) {
      console.log("Current reviews:", product.reviews);
      console.log("Reviews count:", product.reviews.length);
    }
  }, [product]);

  const fetchAvailableCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const coupons = await api.coupons.getActive();
      setAvailableCoupons(Array.isArray(coupons) ? coupons : []);
    } catch (error) {
      console.error("Error fetching coupons:", error);
      setAvailableCoupons([]);
    } finally {
      setLoadingCoupons(false);
    }
  };

  const loadProduct = async () => {
    setLoading(true);
    try {
      const p = await api.products.getById(id);
      const parsedProduct = parseProductData(p);

      setProduct(parsedProduct);
      saveToRecentlyViewed(parsedProduct.id || parsedProduct._id);

      // Set default selections
      if (parsedProduct.sizes?.length > 0) {
        const first = parsedProduct.sizes[0];
        setSelectedSize(typeof first === "object" ? first.name : first);
      } else {
        setSelectedSize("Regular (2.0m)");
      }

      if (parsedProduct.colors?.length > 0) {
        const first = parsedProduct.colors[0];
        setSelectedColor(typeof first === "object" ? first.name : first);
      }

      // Load related products
      const categoryProducts = await api.products.getAll({
        category: parsedProduct.category,
        limit: 8,
      });

      let recs = (categoryProducts.products || []).filter((r) => r._id !== id);
      recs.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      setRelated(recs.slice(0, 4));
    } catch (error) {
      console.error("Error loading product:", error);
      navigate("/collection");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProduct();
  }, [id, navigate]);

  if (loading)
    return (
      <div className="pd-loading">
        <div className="pd-loading-inner">
          <div className="pd-spinner" />
          <p>Curating details…</p>
        </div>
      </div>
    );

  if (!product) return null;

  const images = [product.image, ...(product.images || [])]
    .filter(Boolean)
    .map(normalizeImage)
    .slice(0, 4);

  const isWished = wishlist.includes(product.id);
  const saving = product.originalPrice
    ? product.originalPrice - product.price
    : null;

  const getSizes = () => {
    if (
      !product.sizes ||
      !Array.isArray(product.sizes) ||
      product.sizes.length === 0
    ) {
      return ["Regular (2.0m)", "Large (2.25m)", "XL (2.50m)"];
    }
    return product.sizes;
  };
  const sizes = getSizes();

  const getSelectedSizeStock = () => {
    const sizesArray = Array.isArray(product.sizes) ? product.sizes : [];
    if (sizesArray.length === 0) return product.stock;
    const sel = sizesArray.find(
      (s) => (typeof s === "object" ? s.name : s) === selectedSize,
    );
    return sel && typeof sel === "object" ? sel.stock : product.stock;
  };

  const getSelectedSizePrice = () => {
    const sizesArray = Array.isArray(product.sizes) ? product.sizes : [];
    if (sizesArray.length === 0) return product.price;
    const sel = sizesArray.find(
      (s) => (typeof s === "object" ? s.name : s) === selectedSize,
    );
    return sel && typeof sel === "object" && sel.price
      ? sel.price
      : product.price;
  };

  const selectedStock = getSelectedSizeStock();
  const selectedPrice = getSelectedSizePrice();
  const isInStock = selectedStock > 0 && product.inStock;

  const handleAddToCart = () => {
    requireAuth(() => {
      setCartState("loading");
      addToCart(product, qty, selectedSize, selectedPrice, selectedColor);
      setTimeout(() => {
        setCartState("success");
        setTimeout(() => setCartState("idle"), 2000);
      }, 600);
    }, showToast);
  };

  const handleBuyNow = () => {
    requireAuth(() => {
      addToCart(product, qty, selectedSize, selectedPrice, selectedColor);
      navigate("/cart");
    }, showToast);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      showToast("Please login to submit a review.", "error");
      return;
    }

    if (!reviewForm.comment.trim()) {
      showToast("Please enter a review comment.", "error");
      return;
    }

    setSubmittingRev(true);
    try {
      const reviewData = {
        rating: reviewForm.rating,
        comment: reviewForm.comment.trim(),
        name: user.name || user.email?.split("@")[0] || "Anonymous User",
      };

      console.log("Submitting review:", reviewData);

      // The API service now returns the product directly
      const updatedProduct = await api.products.addReview(
        product.id || product._id,
        reviewData,
      );

      console.log("Received updated product:", updatedProduct);
      console.log("Reviews in updated product:", updatedProduct.reviews);

      showToast("Review submitted! Thank you 🙏", "success");

      // Reset form
      setReviewForm({ rating: 5, comment: "" });

      // Parse the updated product data
      const parsedUpdated = parseProductData(updatedProduct);

      setProduct(parsedUpdated);
      setReviewRefreshKey((prev) => prev + 1);

      // Switch to reviews tab
      setActiveTab("reviews");
    } catch (err) {
      console.error("Error submitting review:", err);
      showToast(
        err.message || "Failed to submit review. Please try again.",
        "error",
      );
    } finally {
      setSubmittingRev(false);
    }
  };

  // Get reviews array safely
  const reviews = Array.isArray(product.reviews) ? product.reviews : [];
  const reviewCount = reviews.length;

  return (
    <div className="pd-wrapper">
      <StickyCartBar
        product={product}
        selectedPrice={selectedPrice}
        selectedSize={selectedSize}
        qty={qty}
        onAddToCart={handleAddToCart}
        isInStock={isInStock}
      />

      {showSizeGuide && (
        <SizeGuideModal onClose={() => setShowSizeGuide(false)} />
      )}

      <div className="pd-container">
        {/* Breadcrumb */}
        <nav className="pd-breadcrumb" aria-label="Breadcrumb">
          <span onClick={() => navigate("/")}>Home</span>
          <span className="pd-breadcrumb-sep">
            <svg
              viewBox="0 0 24 24"
              width="10"
              height="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
          <span
            onClick={() =>
              navigate("/collection", { state: { category: product.category } })
            }
          >
            {product.category}
          </span>
          <span className="pd-breadcrumb-sep">
            <svg
              viewBox="0 0 24 24"
              width="10"
              height="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </span>
          <span className="pd-breadcrumb-current">{product.name}</span>
        </nav>

        {/* Product Layout */}
        <div className="pd-layout">
          {/* ── Gallery ── */}
          <div className="pd-gallery-wrapper">
            <div className="pd-gallery">
              <div className="pd-img-counter">
                {activeImg + 1} / {images.length}
              </div>
              <div className="pd-gallery-main">
                <ZoomableImage src={images[activeImg]} alt={product.name} />
                {!isInStock && (
                  <div className="pd-oos-overlay">
                    <span className="pd-oos-badge">Out of Stock</span>
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="pd-gallery-thumbs">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      className={`pd-thumb ${i === activeImg ? "is-active" : ""}`}
                      onClick={() => setActiveImg(i)}
                      aria-label={`View image ${i + 1}`}
                    >
                      <img src={img} alt={`View ${i + 1}`} loading="lazy" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <AuthenticityBadge />
          </div>

          {/* ── Product Info ── */}
          <div className="pd-info">
            <div className="pd-info-header">
              <span className="pd-brand">{product.brand}</span>
              {product.badge && (
                <span className="pd-badge">{product.badge}</span>
              )}
            </div>

            <h1 className="pd-title">{product.name}</h1>

            {reviewCount > 0 && (
              <button
                className="pd-rating"
                onClick={() => setActiveTab("reviews")}
                aria-label="Jump to reviews"
              >
                <div className="pd-stars">
                  {[...Array(5)].map((_, i) =>
                    i < Math.round(product.rating) ? (
                      <FaStar key={i} />
                    ) : (
                      <FaRegStar key={i} />
                    ),
                  )}
                </div>
                <span className="pd-review-count">
                  ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})
                </span>
                <span className="pd-rating-link">Read reviews</span>
              </button>
            )}

            {/* Price */}
            <div className="pd-price-block">
              <div className="pd-price-row">
                <span className="pd-price-sale">
                  ₹{selectedPrice?.toLocaleString()}
                </span>
                {product.originalPrice && (
                  <>
                    <span className="pd-price-original">
                      ₹{product.originalPrice?.toLocaleString()}
                    </span>
                    {saving && (
                      <span className="pd-price-save">Save ₹{saving}</span>
                    )}
                  </>
                )}
              </div>
              <p className="pd-tax-note">
                Inclusive of all taxes · Free shipping above ₹1,000
              </p>
            </div>

            <TrustBadges />
            <div className="pd-rule" />

            {/* Sizes */}
            <div className="pd-option-group">
              <div className="pd-option-header">
                <span className="pd-option-label">Select Size</span>
                <button
                  className="pd-size-guide-trigger"
                  onClick={() => setShowSizeGuide(true)}
                >
                  <FaRuler size={10} /> Size Guide
                </button>
              </div>
              <div className="pd-size-options">
                {Array.isArray(sizes) &&
                  sizes.map((s) => {
                    const sizeName = typeof s === "object" ? s.name : s;
                    const sizeStock =
                      typeof s === "object"
                        ? s.stock || product.stock
                        : product.stock;
                    const isDisabled = sizeStock === 0;
                    return (
                      <button
                        key={sizeName}
                        className={`pd-size-btn ${selectedSize === sizeName ? "is-selected" : ""} ${isDisabled ? "is-disabled" : ""}`}
                        onClick={() => !isDisabled && setSelectedSize(sizeName)}
                        disabled={isDisabled}
                      >
                        {sizeName}
                      </button>
                    );
                  })}
              </div>
              {isInStock && selectedStock < 10 && (
                <p className="pd-stock-warning">
                  <span className="pd-stock-flame">🔥</span>
                  Only {selectedStock} left in this size
                </p>
              )}
            </div>

            {/* Colours */}
            {(() => {
              const colors =
                product.colors &&
                Array.isArray(product.colors) &&
                product.colors.length > 0
                  ? product.colors
                  : DEFAULT_COLORS;
              return (
                <div className="pd-option-group">
                  <div className="pd-option-header">
                    <span className="pd-option-label">
                      Colour
                      {selectedColor && (
                        <span className="pd-selected-colour-name">
                          {" "}
                          — {selectedColor}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="pd-colour-options">
                    {colors.map((c) => {
                      const colorName = typeof c === "object" ? c.name : c;
                      const colorHex =
                        typeof c === "object"
                          ? c.hex || c.color || "#888"
                          : "#888";
                      const isSelected = selectedColor === colorName;
                      const isLight = /^#([0-9a-f]{3}){1,2}$/i.test(colorHex)
                        ? (() => {
                            const h = colorHex.replace("#", "");
                            const r = parseInt(
                              h.length === 3 ? h[0] + h[0] : h.slice(0, 2),
                              16,
                            );
                            const g = parseInt(
                              h.length === 3 ? h[1] + h[1] : h.slice(2, 4),
                              16,
                            );
                            const b = parseInt(
                              h.length === 3 ? h[2] + h[2] : h.slice(4, 6),
                              16,
                            );
                            return (r * 299 + g * 587 + b * 114) / 1000 > 200;
                          })()
                        : false;
                      return (
                        <button
                          key={colorName}
                          className={`pd-colour-swatch ${isSelected ? "is-selected" : ""} ${isLight ? "is-light" : ""}`}
                          style={{ "--swatch-color": colorHex }}
                          onClick={() => setSelectedColor(colorName)}
                          aria-label={`Select colour: ${colorName}`}
                          title={colorName}
                        >
                          <span className="pd-swatch-inner" />
                          {isSelected && (
                            <span className="pd-swatch-check">
                              <svg
                                viewBox="0 0 12 12"
                                width="10"
                                height="10"
                                fill="none"
                                stroke={isLight ? "#111" : "#fff"}
                                strokeWidth="2"
                                strokeLinecap="round"
                              >
                                <polyline points="2,6 5,9 10,3" />
                              </svg>
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Quantity */}
            <div className="pd-option-group">
              <span className="pd-option-label">Quantity</span>
              <div className="pd-qty">
                <button
                  className="pd-qty-btn"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                >
                  −
                </button>
                <input
                  className="pd-qty-input"
                  type="number"
                  value={qty}
                  readOnly
                />
                <button
                  className="pd-qty-btn"
                  onClick={() => setQty((q) => Math.min(selectedStock, q + 1))}
                >
                  +
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="pd-actions">
              <div className="pd-actions-row">
                <button
                  className={`pd-btn-cart ${cartState === "success" ? "is-success" : ""}`}
                  onClick={handleAddToCart}
                  disabled={!isInStock || cartState === "loading"}
                >
                  {cartState === "loading" && (
                    <span className="pd-btn-spinner" />
                  )}
                  {cartState === "success" && (
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                  {cartState === "idle" && (
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                    >
                      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <path d="M16 10a4 4 0 0 1-8 0" />
                    </svg>
                  )}
                  {cartState === "success"
                    ? "Added!"
                    : isInStock
                      ? "Add to Cart"
                      : "Out of Stock"}
                </button>

                <button
                  className={`pd-btn-wishlist ${isWished ? "is-wished" : ""}`}
                  onClick={() => toggleWishlist(product.id)}
                  aria-label={
                    isWished ? "Remove from wishlist" : "Add to wishlist"
                  }
                >
                  {isWished ? <FaHeart /> : <FaRegHeart />}
                </button>
              </div>

              {isInStock && (
                <button className="pd-btn-buynow" onClick={handleBuyNow}>
                  Buy It Now
                </button>
              )}
            </div>

            <DeliveryBar />

            <CouponStrip
              showToast={showToast}
              availableCoupons={availableCoupons}
              loadingCoupons={loadingCoupons}
            />

            {/* Social share */}
            <div className="pd-share">
              <span>Share</span>
              <div className="pd-share-icons">
                <a
                  href="#facebook"
                  className="pd-share-btn"
                  aria-label="Share on Facebook"
                >
                  <FaFacebookF size={20} />
                </a>
                <a
                  href="#instagram"
                  className="pd-share-btn"
                  aria-label="Share on Instagram"
                >
                  <FaInstagram size={20} />
                </a>
                <a
                  href="#twitter"
                  className="pd-share-btn"
                  aria-label="Share on Twitter"
                >
                  <FaTwitter size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <KeyIngredients />
        <HowToUse />
        <FabricStory />

        {/* ── Tabbed Content ── */}
        <div className="pd-tabs-section">
          <div className="pd-tabs-header">
            {[
              ["desc", "Description"],
              ["ship", "Shipping"],
              ["reviews", `Reviews (${reviewCount})`],
            ].map(([k, label]) => (
              <button
                key={k}
                className={`pd-tab-btn ${activeTab === k ? "is-active" : ""}`}
                onClick={() => setActiveTab(k)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="pd-tab-content">
            {activeTab === "desc" && (
              <div className="pd-tab-pane pd-fade-in">
                <h3>About {product.name}</h3>
                <p>
                  {product.description ||
                    "Meticulously crafted by specialists who combine designs with colours to give you that pep in your step. Made from 100% pure cotton, perfect for everyday comfort and festive occasions alike."}
                </p>
                <ul className="pd-styled-list">
                  {product.features?.length > 0 ? (
                    product.features.map((f, i) => <li key={i}>{f}</li>)
                  ) : (
                    <>
                      <li>100% Pure Cotton fabric</li>
                      <li>Vibrant, long-lasting colours</li>
                      <li>Machine washable & easy care</li>
                      <li>Manufactured ethically in Tamil Nadu</li>
                    </>
                  )}
                </ul>
              </div>
            )}

            {activeTab === "ship" && (
              <div className="pd-tab-pane pd-fade-in">
                <h3>Shipping & Returns</h3>
                <p>
                  We ship across India via trusted courier partners. Orders are
                  typically processed within 1–2 business days.
                </p>
                <ul className="pd-styled-list">
                  <li>
                    <strong>Free Shipping</strong> on orders above ₹1,000
                  </li>
                  <li>Standard delivery: 5–7 business days</li>
                  <li>Express delivery: 2–3 business days (₹99 extra)</li>
                </ul>
              </div>
            )}

            {activeTab === "reviews" && (
              <div className="pd-tab-pane pd-fade-in" key={reviewRefreshKey}>
                <div className="pd-reviews-header">
                  <h3>Customer Reviews</h3>
                </div>
                <ReviewSummary reviews={reviews} />
                <div className="pd-reviews-list">
                  {reviews.length === 0 ? (
                    <p className="pd-empty-state">
                      No reviews yet. Be the first to share your thoughts!
                    </p>
                  ) : (
                    reviews.map((r, i) => (
                      <div
                        key={`${i}-${r.createdAt || Date.now()}`}
                        className="pd-review-card"
                      >
                        <div className="pd-review-meta">
                          <div className="pd-review-meta-left">
                            <span className="pd-reviewer-avatar">
                              {r.name?.charAt(0) || "U"}
                            </span>
                            <div>
                              <span className="pd-reviewer-name">
                                {r.name || "Anonymous"}
                              </span>
                              <span className="pd-review-date">
                                {r.createdAt
                                  ? new Date(r.createdAt).toLocaleDateString(
                                      "en-IN",
                                      {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                      },
                                    )
                                  : "Just now"}
                              </span>
                            </div>
                          </div>
                          <span className="pd-verified-badge">
                            <MdVerified size={12} /> Verified
                          </span>
                        </div>
                        <div className="pd-review-stars">
                          {[...Array(5)].map((_, idx) =>
                            idx < r.rating ? (
                              <FaStar key={idx} />
                            ) : (
                              <FaRegStar key={idx} />
                            ),
                          )}
                        </div>
                        <p className="pd-review-text">{r.comment}</p>
                      </div>
                    ))
                  )}
                </div>
                {user ? (
                  <form
                    onSubmit={handleSubmitReview}
                    className="pd-review-form"
                  >
                    <h4>Write a Review</h4>
                    <div className="pd-form-group">
                      <label>Your Rating</label>
                      <div className="pd-rating-input">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button
                            type="button"
                            key={n}
                            onClick={() =>
                              setReviewForm((p) => ({ ...p, rating: n }))
                            }
                            className={
                              n <= reviewForm.rating ? "is-active" : ""
                            }
                            aria-label={`${n} star${n !== 1 ? "s" : ""}`}
                          >
                            <FaStar />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="pd-form-group">
                      <label>Your Review</label>
                      <textarea
                        value={reviewForm.comment}
                        onChange={(e) =>
                          setReviewForm((p) => ({
                            ...p,
                            comment: e.target.value,
                          }))
                        }
                        placeholder="Share your experience with this product…"
                        required
                        rows={4}
                      />
                    </div>
                    <button
                      type="submit"
                      className="pd-btn-submit-review"
                      disabled={submittingRev}
                    >
                      {submittingRev ? (
                        <>
                          <span className="pd-btn-spinner" /> Submitting…
                        </>
                      ) : (
                        "Post Review"
                      )}
                    </button>
                  </form>
                ) : (
                  <div className="pd-login-prompt">
                    <p>Please log in to write a review.</p>
                    <Link className="pd-login-link" to="/account">
                      Log In →
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <RecentlyViewed currentProductId={product.id || product._id} />
        <YouMayAlsoLike
          currentProductId={product.id || product._id}
          currentCategory={product.category}
          currentTags={product.tags || []}
        />

        {related.length > 0 && (
          <section className="pd-related-section">
            <div className="pd-related-header">
              <span className="pd-related-eyebrow">Discover More</span>
              <h2 className="pd-related-title">Complete Your Look</h2>
            </div>
            <div className="pd-related-grid">
              {related.map((p) => (
                <ProductCard key={p._id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
